import { useCallback, useState } from 'react';
import { Platform } from 'react-native';
import DocumentPicker from 'react-native-document-picker';
import {
  Asset,
  CameraOptions,
  ImageLibraryOptions,
  launchCamera,
  launchImageLibrary,
} from 'react-native-image-picker';

import { useAuth } from '../hooks/useAuth';

interface UploadOptions {
  uri: string;
  type: string;
  name: string;
  size?: number;
}

interface UploadResult {
  id: string;
  url: string;
  originalName: string;
  size: number;
  mimeType: string;
  thumbnailUrl?: string;
}

class UploadService {
  private apiBaseUrl = process.env.API_URL || 'http://localhost:4102';

  async uploadFile(file: UploadOptions, token: string): Promise<UploadResult> {
    const formData = new FormData();

    // Prepare the file data
    const fileInfo = {
      uri: file.uri,
      type: file.type,
      name: file.name,
    } as any;

    // Android compatibility
    if (Platform.OS === 'android') {
      fileInfo.uri = file.uri.startsWith('file://') ? file.uri : `file://${file.uri}`;
    }

    formData.append('file', fileInfo);

    const response = await fetch(`${this.apiBaseUrl}/uploads`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    return await response.json();
  }

  async uploadImage(uri: string, token: string): Promise<UploadResult> {
    // Get file info from URI
    const fileInfo = await this.getFileInfo(uri);

    return await this.uploadFile(
      {
        uri,
        type: 'image/jpeg',
        name: `image_${Date.now()}.jpg`,
        size: fileInfo.size,
      },
      token,
    );
  }

  async uploadVideo(uri: string, token: string): Promise<UploadResult> {
    const fileInfo = await this.getFileInfo(uri);

    return await this.uploadFile(
      {
        uri,
        type: 'video/mp4',
        name: `video_${Date.now()}.mp4`,
        size: fileInfo.size,
      },
      token,
    );
  }

  async uploadDocument(uri: string, token: string): Promise<UploadResult> {
    const fileInfo = await this.getFileInfo(uri);

    return await this.uploadFile(
      {
        uri,
        type: fileInfo.type || 'application/octet-stream',
        name: `document_${Date.now()}`,
        size: fileInfo.size,
      },
      token,
    );
  }

  async getMyFiles(token: string, limit: number = 50, offset: number = 0): Promise<UploadResult[]> {
    const response = await fetch(`${this.apiBaseUrl}/uploads/my?limit=${limit}&offset=${offset}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch files');
    }

    return await response.json();
  }

  async deleteFile(fileId: string, token: string): Promise<boolean> {
    const response = await fetch(`${this.apiBaseUrl}/uploads/${fileId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.ok;
  }

  private async getFileInfo(_uri: string): Promise<{ size: number; type?: string }> {
    // This would typically use a library like react-native-fs
    // For now, returning placeholder values
    return {
      size: 0,
      type: 'application/octet-stream',
    };
  }

  // Format file size for display
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  }

  // Get file icon based on type
  getFileIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) {
      return '🖼️';
    } else if (mimeType.startsWith('video/')) {
      return '🎬';
    } else if (mimeType.startsWith('audio/')) {
      return '🎵';
    } else if (mimeType.includes('pdf')) {
      return '📄';
    } else if (mimeType.includes('word') || mimeType.includes('document')) {
      return '📝';
    } else if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {
      return '📊';
    } else if (mimeType.includes('zip') || mimeType.includes('compressed')) {
      return '📦';
    } else {
      return '📎';
    }
  }

  // Validate file size
  validateFileSize(size: number, maxSize: number = 50 * 1024 * 1024): boolean {
    return size <= maxSize;
  }

  // Validate file type
  validateFileType(mimeType: string, allowedTypes: string[]): boolean {
    return allowedTypes.some((type) => mimeType.startsWith(type));
  }

  // Get file extension
  getFileExtension(fileName: string): string {
    return fileName.split('.').pop()?.toLowerCase() || '';
  }
}

// Create a singleton instance
const uploadServiceInstance = new UploadService();

export default uploadServiceInstance;

// Also export as named export for internal use
export { uploadServiceInstance };

// React Hook for file operations
export function useFileUpload() {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const uploadImage = useCallback(
    async (uri: string): Promise<UploadResult | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setUploading(true);
      setProgress(0);
      setError(null);

      try {
        // Simulate progress
        const progressInterval = setInterval(() => {
          setProgress((prev) => Math.min(prev + 10, 90));
        }, 100);

        const result = await uploadServiceInstance.uploadImage(uri, token);

        clearInterval(progressInterval);
        setProgress(100);

        return result;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed');
        return null;
      } finally {
        setUploading(false);
      }
    },
    [token],
  );

  const uploadVideo = useCallback(
    async (uri: string): Promise<UploadResult | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setUploading(true);
      setProgress(0);
      setError(null);

      try {
        const progressInterval = setInterval(() => {
          setProgress((prev) => Math.min(prev + 5, 90));
        }, 100);

        const result = await uploadServiceInstance.uploadVideo(uri, token);

        clearInterval(progressInterval);
        setProgress(100);

        return result;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed');
        return null;
      } finally {
        setUploading(false);
      }
    },
    [token],
  );

  const uploadDocument = useCallback(
    async (uri: string): Promise<UploadResult | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setUploading(true);
      setProgress(0);
      setError(null);

      try {
        const result = await uploadServiceInstance.uploadDocument(uri, token);

        setProgress(100);

        return result;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed');
        return null;
      } finally {
        setUploading(false);
      }
    },
    [token],
  );

  const pickImage = useCallback(async (): Promise<Asset | null> => {
    const options: ImageLibraryOptions = {
      mediaType: 'photo',
      quality: 0.8,
      maxWidth: 1920,
      maxHeight: 1080,
    };

    try {
      const result = await launchImageLibrary(options);

      if (result.didCancel) {
        return null;
      }

      if (result.errorCode) {
        setError(result.errorMessage || 'Failed to pick image');
        return null;
      }

      const asset = result.assets?.[0];
      return asset || null;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to pick image');
      return null;
    }
  }, []);

  const takePhoto = useCallback(async (): Promise<Asset | null> => {
    const options: CameraOptions = {
      mediaType: 'photo',
      quality: 0.8,
      maxWidth: 1920,
      maxHeight: 1080,
    };

    try {
      const result = await launchCamera(options);

      if (result.didCancel) {
        return null;
      }

      if (result.errorCode) {
        setError(result.errorMessage || 'Failed to take photo');
        return null;
      }

      const asset = result.assets?.[0];
      return asset || null;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to take photo');
      return null;
    }
  }, []);

  const pickDocument = useCallback(async (): Promise<string | null> => {
    try {
      const result = await DocumentPicker.pick({
        type: [DocumentPicker.types.allFiles],
      });

      return result[0].uri;
    } catch (err) {
      if (DocumentPicker.isCancel(err)) {
        return null;
      }
      setError(err instanceof Error ? err.message : 'Failed to pick document');
      return null;
    }
  }, []);

  const deleteFile = useCallback(
    async (fileId: string): Promise<boolean> => {
      if (!token) {
        setError('Not authenticated');
        return false;
      }

      try {
        return await uploadServiceInstance.deleteFile(fileId, token);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Delete failed');
        return false;
      }
    },
    [token],
  );

  const getMyFiles = useCallback(
    async (limit: number = 50, offset: number = 0): Promise<UploadResult[]> => {
      if (!token) {
        setError('Not authenticated');
        return [];
      }

      try {
        return await uploadServiceInstance.getMyFiles(token, limit, offset);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch files');
        return [];
      }
    },
    [token],
  );

  return {
    uploading,
    progress,
    error,
    uploadImage,
    uploadVideo,
    uploadDocument,
    pickImage,
    takePhoto,
    pickDocument,
    deleteFile,
    getMyFiles,
    clearError: () => setError(null),
  };
}

// Helper hook for media picker
export function useMediaPicker() {
  const { pickImage, takePhoto } = useFileUpload();

  const handlePickFromLibrary = async () => {
    const asset = await pickImage();
    if (asset?.uri) {
      return asset.uri;
    }
    return null;
  };

  const handleTakePhoto = async () => {
    const asset = await takePhoto();
    if (asset?.uri) {
      return asset.uri;
    }
    return null;
  };

  return {
    pickFromLibrary: handlePickFromLibrary,
    takePhoto: handleTakePhoto,
  };
}
